import { useState, useEffect } from 'react';
import { CheckCircle, XCircle, RefreshCw, Loader2, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { motion, AnimatePresence } from 'framer-motion';
import SubPageHeader from '../SubPageHeader';
import { useOdooAuth } from '@/lib/OdooAuthContext';
import { api, getOdooOrigin, getOdooDatabase, setApiConfig } from '@/api/apiClient';
import { toast } from 'sonner';

export default function OdooConnectionSection({ onBack }) {
  const { odooSession } = useOdooAuth();

  // Connection-test state
  const [connStatus, setConnStatus] = useState(null);
  const [testing, setTesting] = useState(false);
  const [testSteps, setTestSteps] = useState([]);
  const [diagResult, setDiagResult] = useState(null);
  const [diagLoading, setDiagLoading] = useState(false);

  // Editable connection settings
  const [serverUrl, setServerUrl] = useState(getOdooOrigin() || '');
  const [apiBase, setApiBase] = useState(
    localStorage.getItem('dashboard_api_base_path') || 'dashboard/api'
  );
  const [database, setDatabase] = useState(getOdooDatabase() || '');

  // Run a connection test on first mount so we have a live indicator
  useEffect(() => {
    testConnection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function testConnection() {
    setTesting(true);
    setTestSteps([]);
    setConnStatus(null);
    const steps = [];
    const addStep = (key, label, ok, detail) => {
      steps.push({ key, label, ok, detail });
      setTestSteps([...steps]);
    };
    try {
      const t0 = Date.now();
      const res = await api.get('/diagnostics/info');
      const ms = Date.now() - t0;
      if (res?.success) {
        const info = res.data || {};
        addStep('ping', 'الاتصال بالخادم', true, `${ms}ms`);
        addStep('auth', 'المصادقة', true, `UID: ${info.uid}`);
        addStep('db', 'قاعدة البيانات', true, info.db || '—');
        addStep('company', 'الشركة', true, info.company || '—');
        setConnStatus({ success: true, ms, ...info });
      } else {
        addStep('ping', 'الاتصال بالخادم', false, res?.error || 'فشل');
        setConnStatus({ success: false, error: res?.error });
      }
    } catch (e) {
      addStep('ping', 'الاتصال بالخادم', false, e.message);
      setConnStatus({ success: false, error: e.message });
    }
    setTesting(false);
  }

  async function runDiagnostics() {
    setDiagLoading(true);
    setDiagResult(null);
    try {
      const res = await api.get('/diagnostics/full');
      if (res?.success) setDiagResult(res.data);
      else setDiagResult({ error: res?.error || 'فشل' });
    } catch (e) {
      setDiagResult({ error: e.message });
    }
    setDiagLoading(false);
  }

  function saveSettings() {
    setApiConfig({
      serverUrl: serverUrl.trim() || null,
      apiBasePath: apiBase.trim() || 'dashboard/api',
      database: database.trim() || null,
    });
    toast.success('تم حفظ الإعدادات');
    // Re-test against the new endpoint
    testConnection();
  }

  // Display values
  let displayHost = '—';
  try {
    const raw = serverUrl || window.location.origin;
    displayHost = new URL(raw).host;
  } catch {
    displayHost = serverUrl || '—';
  }

  return (
    <div className="flex flex-col min-h-full" dir="rtl">
      <SubPageHeader title="الاتصال بـ Odoo" onBack={onBack} />
      <div className="p-4 space-y-4">

        {/* Live Status */}
        <div
          className={`rounded-2xl p-4 border ${
            connStatus?.success === false
              ? 'bg-red-50 border-red-200'
              : 'bg-emerald-50 border-emerald-200'
          }`}
        >
          <div className="flex items-center gap-3">
            <div
              className={`w-3 h-3 rounded-full animate-pulse ${
                connStatus?.success === false ? 'bg-red-500' : 'bg-emerald-500'
              }`}
            />
            <div>
              <p className="font-bold text-sm">
                {connStatus?.success === false ? '❌ منقطع' : '✅ متصل'}
              </p>
              {connStatus?.ms && (
                <p className="text-xs text-muted-foreground">
                  زمن الاستجابة: {connStatus.ms}ms
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Server Info */}
        <div className="bg-card border border-border rounded-2xl p-4 space-y-1">
          <p className="font-bold text-sm text-foreground mb-3">معلومات الخادم</p>
          {[
            ['🌐', 'رابط الخادم', displayHost],
            ['🗄️', 'قاعدة البيانات', database || '—'],
            ['⚙️', 'إصدار Odoo', connStatus?.odoo_version || '17 Community'],
            ['🔒', 'البروتوكول', 'REST / HTTPS'],
            ['👤', 'المستخدم الحالي', odooSession?.name || '—'],
          ].map(([icon, label, value]) => (
            <div
              key={label}
              className="flex items-center justify-between py-2.5 border-b border-border last:border-0"
            >
              <div className="flex items-center gap-2">
                <span className="text-sm">{icon}</span>
                <span className="text-sm text-muted-foreground">{label}</span>
              </div>
              <span className="text-sm font-semibold text-foreground" dir="ltr">
                {value}
              </span>
            </div>
          ))}
        </div>

        {/* Editable Settings */}
        <div className="bg-card border border-border rounded-2xl p-4 space-y-3">
          <p className="font-bold text-sm text-foreground">إعدادات الاتصال</p>
          <p className="text-xs text-muted-foreground">
            تغيير هذه الإعدادات يؤثر فورًا على جميع الطلبات. إذا غيّرت قاعدة
            البيانات أو الخادم، قد تحتاج إلى تسجيل خروج وإعادة دخول.
          </p>

          <div className="space-y-1.5">
            <label className="text-xs font-medium text-foreground">رابط الخادم</label>
            <Input
              type="url"
              value={serverUrl}
              onChange={(e) => setServerUrl(e.target.value)}
              placeholder="https://your-odoo.example.com"
              className="rounded-xl text-sm"
              dir="ltr"
            />
            <p className="text-xs text-muted-foreground">
              اتركه فارغًا لاستخدام نفس مصدر الموقع (مفيد عند تشغيل الموقع عبر
              Nginx بجانب Odoo).
            </p>
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-medium text-foreground">مسار API الأساسي</label>
            <Input
              value={apiBase}
              onChange={(e) => setApiBase(e.target.value)}
              placeholder="dashboard/api"
              className="rounded-xl text-sm"
              dir="ltr"
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-medium text-foreground">قاعدة البيانات</label>
            <Input
              value={database}
              onChange={(e) => setDatabase(e.target.value)}
              placeholder="database-name"
              className="rounded-xl text-sm"
              dir="ltr"
            />
          </div>

          <Button onClick={saveSettings} className="w-full gap-2 rounded-xl">
            <Save className="w-4 h-4" /> حفظ الإعدادات
          </Button>
        </div>

        {/* Connection Test */}
        <div className="bg-card border border-border rounded-2xl p-4 space-y-3">
          <p className="font-bold text-sm text-foreground">اختبار الاتصال</p>
          <Button
            onClick={testConnection}
            disabled={testing}
            className="w-full gap-2 rounded-xl"
            variant="outline"
          >
            <RefreshCw className={`w-4 h-4 ${testing ? 'animate-spin' : ''}`} />
            {testing ? 'جاري الاختبار...' : '🔄 اختبار الاتصال'}
          </Button>

          <AnimatePresence>
            {testSteps.length > 0 && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="space-y-2"
              >
                {testSteps.map((step, i) => (
                  <motion.div
                    key={step.key}
                    initial={{ opacity: 0, x: 10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.1 }}
                    className="flex items-center gap-2 py-2 px-3 rounded-xl bg-muted/40"
                  >
                    {step.ok ? (
                      <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                    ) : (
                      <XCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                    )}
                    <span className="text-sm font-medium flex-1">{step.label}</span>
                    <span className="text-xs text-muted-foreground">
                      {step.detail}
                    </span>
                  </motion.div>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Full Diagnostics */}
        <div className="bg-card border border-border rounded-2xl p-4 space-y-3">
          <p className="font-bold text-sm text-foreground">تشخيص شامل</p>
          <Button
            onClick={runDiagnostics}
            disabled={diagLoading}
            className="w-full gap-2 rounded-xl"
            variant="outline"
          >
            {diagLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : '🩺'}
            {diagLoading ? 'جاري التشخيص...' : 'تشغيل التشخيص الكامل'}
          </Button>

          {diagResult && !diagResult.error && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-2"
            >
              <div className="rounded-xl p-3 bg-emerald-50 flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-600" />
                <span className="text-sm font-semibold">قاعدة البيانات</span>
                <span className="text-xs text-muted-foreground mr-auto">
                  {diagResult.db || '—'}
                </span>
              </div>
              <div className="rounded-xl p-3 bg-emerald-50 flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-600" />
                <span className="text-sm font-semibold">المستخدم</span>
                <span className="text-xs text-muted-foreground mr-auto">
                  {diagResult.user || '—'} (UID: {diagResult.uid})
                </span>
              </div>
              {Array.isArray(diagResult.endpoints) && (
                <div className="bg-muted/30 rounded-xl p-3 space-y-1">
                  <p className="text-xs font-bold text-foreground mb-1">
                    {diagResult.endpoints.length} نقطة نهاية متاحة
                  </p>
                  <div className="max-h-48 overflow-y-auto space-y-1">
                    {diagResult.endpoints.map((ep) => (
                      <p
                        key={ep}
                        className="text-xs font-mono text-muted-foreground"
                        dir="ltr"
                      >
                        {ep}
                      </p>
                    ))}
                  </div>
                </div>
              )}
            </motion.div>
          )}

          {diagResult?.error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-xs text-red-700">
              {diagResult.error}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
